/**
 * The stamp itself (docs/POSTAGE_PLAN.md §3): a white perforated margin around
 * a coloured panel with the class mark — the shape of a real stamp — so it
 * reads on any background. `FrankedStamp` adds the ink postmark (a circular
 * date cancel with wavy lines) overlapping its edge, the way the sorting office
 * franks a letter.
 *
 * Panels are dark on purpose: 2nd class is post-office blue, 1st class is
 * sealing-wax, Express is ink.
 */

import { useId } from 'react';
import { POSTAGE_LABELS, POSTAGE_STAMP_MARK, type PostageClass } from '@toiyp/shared';

const ART: Record<PostageClass, { panel: string; frame: string; ink: string; sub: string }> = {
  second: { panel: '#2F4E7A', frame: '#8FA9CF', ink: '#F4F7FC', sub: '#C9D6EA' },
  first: { panel: '#7B5244', frame: '#D9A894', ink: '#FDF0E8', sub: '#F0CFC1' },
  express: { panel: '#2C2420', frame: '#C8907C', ink: '#F0C2A8', sub: '#D9B8A6' },
};

const PAPER = '#FFFDF9';

/** Width in px; the stamp is 6:7, like a definitive. */
const SIZES = { mini: 16, small: 24, medium: 36, sheet: 50, envelope: 72, large: 92 } as const;
export type StampSize = keyof typeof SIZES;

/** Artwork for a custom stamp (toiyp Plus). Only the picture changes; the class mark stays. */
export interface StampArtwork {
  imageUrl: string;
  name?: string;
}

/** The class mark printed on a design's corner. Short: it sits on someone's art. */
const DESIGN_MARK: Record<PostageClass, string> = { second: '2nd', first: '1st', express: 'E' };

/**
 * A custom stamp: the uploaded artwork as it is (its own perforated edge and
 * transparent background), flat in the stamp's box — no shading, no tilt —
 * with the postage class as a small mark in the corner.
 */
export function DesignStamp({
  cls,
  design,
  size = 'small',
  decorative = true,
}: {
  cls: PostageClass;
  design: StampArtwork;
  size?: StampSize;
  decorative?: boolean;
}) {
  const w = SIZES[size];
  const h = Math.round((w * 70) / 60);
  return (
    <span
      className={`postage-stamp design-stamp design-stamp--${cls} postage-stamp--${size}`}
      style={{ width: w, height: h }}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : `${POSTAGE_LABELS[cls]} stamp${design.name ? `, ${design.name}` : ''}`}
    >
      <img className="design-stamp__art" src={design.imageUrl} alt="" draggable={false} loading="lazy" />
      {size !== 'mini' && <span className="design-stamp__mark">{DESIGN_MARK[cls]}</span>}
    </span>
  );
}

export function PostageStamp({
  cls,
  size = 'small',
  postmark,
  decorative = true,
  design,
}: {
  cls: PostageClass;
  size?: StampSize;
  /** The date on a postmark drawn inside the stamp — large stamps only. */
  postmark?: Date;
  /** Hidden from assistive tech when the label is already in the text beside it. */
  decorative?: boolean;
  /** Custom artwork: printed in place of the class panel, the class kept as a corner mark. */
  design?: StampArtwork | null;
}) {
  if (design) return <DesignStamp cls={cls} design={design} size={size} decorative={decorative} />;
  return <ClassStamp cls={cls} size={size} postmark={postmark} decorative={decorative} />;
}

function ClassStamp({
  cls,
  size,
  postmark,
  decorative,
}: {
  cls: PostageClass;
  size: StampSize;
  postmark?: Date;
  decorative: boolean;
}) {
  const id = useId().replace(/:/g, '');
  const art = ART[cls];
  const w = SIZES[size];
  const h = Math.round((w * 70) / 60);
  // Perforations: holes punched along every edge of a 60×70 sheet.
  const holes: [number, number][] = [];
  for (let x = 4; x <= 56; x += 6.5) holes.push([x, 0], [x, 70]);
  for (let y = 4; y <= 66; y += 6.5) holes.push([0, y], [60, y]);
  const showWords = size !== 'mini';

  return (
    <svg
      className={`postage-stamp postage-stamp--${cls} postage-stamp--${size}`}
      width={w}
      height={h}
      viewBox="0 0 60 70"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : `${POSTAGE_LABELS[cls]} stamp`}
    >
      <defs>
        <mask id={`perf-${id}`}>
          <rect width="60" height="70" fill="#fff" />
          {holes.map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="2.7" fill="#000" />
          ))}
        </mask>
      </defs>
      <g mask={`url(#perf-${id})`}>
        <rect width="60" height="70" fill={PAPER} />
        <rect x="5" y="5" width="50" height="60" rx="1.5" fill={art.panel} />
        <rect x="8.5" y="8.5" width="43" height="53" rx="1" fill="none" stroke={art.frame} strokeWidth="1" />
        {cls === 'express' && showWords && (
          // Speed lines under the mark — the one thing Express does differently.
          <g stroke={art.frame} strokeWidth="1.6" strokeLinecap="round">
            <path d="M17 43 H43" />
            <path d="M21 47 H39" />
          </g>
        )}
        <text
          x="30"
          y={showWords ? 36 : 42}
          textAnchor="middle"
          fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
          fontWeight="800"
          fontSize={cls === 'express' ? (showWords ? 14 : 17) : showWords ? 19 : 24}
          letterSpacing={cls === 'express' ? 0.5 : 0}
          fill={art.ink}
        >
          {POSTAGE_STAMP_MARK[cls]}
        </text>
        {showWords && (
          <text
            x="30"
            y="56"
            textAnchor="middle"
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
            fontWeight="600"
            fontSize="6.5"
            letterSpacing="1.2"
            fill={art.sub}
          >
            TOIYP
          </text>
        )}
      </g>
      {postmark && size === 'large' && (
        <g color="#2C2420">
          <PostmarkMarks date={postmark} cx={46} cy={16} r={11.5} />
        </g>
      )}
    </svg>
  );
}

/** The ink cancellation: a double ring with the date, and wavy lines off it. */
function PostmarkMarks({ date, cx, cy, r }: { date: Date; cx: number; cy: number; r: number }) {
  const day = date.toLocaleDateString([], { day: 'numeric', month: 'short' }).toUpperCase();
  return (
    <g className="postmark" fill="none" stroke="currentColor" strokeWidth="1.1" opacity="0.75">
      <circle cx={cx} cy={cy} r={r} />
      <circle cx={cx} cy={cy} r={r * 0.8} strokeWidth="0.5" />
      <text
        x={cx}
        y={cy + 2}
        textAnchor="middle"
        fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
        fontWeight="700"
        fontSize={r * 0.42}
        fill="currentColor"
        stroke="none"
      >
        {day}
      </text>
      {[-4.5, 0, 4.5].map((dy) => (
        <path key={dy} d={`M${cx + r + 1} ${cy + dy} q4 -3 8 0 t8 0 t8 0 t8 0`} />
      ))}
    </g>
  );
}

/**
 * A stamp franked with a postmark — the ring straddles the stamp's left edge
 * and the cancel lines run across it, as a sorting office leaves it. Unfranked
 * (no `date`) while the letter still waits in the postbox.
 */
export function FrankedStamp({
  cls,
  date,
  size = 'medium',
  design,
}: {
  cls: PostageClass;
  /** When it was franked (collected). Null: not franked yet. */
  date: Date | null;
  size?: Exclude<StampSize, 'mini'>;
  design?: StampArtwork | null;
}) {
  const w = SIZES[size];
  const h = Math.round((w * 70) / 60);
  // The postmark sits in a box 14px wider than the stamp, overhanging its left.
  const pad = Math.round(w * 0.4);
  return (
    <span className={`franked-stamp franked-stamp--${size}`} style={{ width: w + pad, height: h }}>
      <PostageStamp cls={cls} size={size} design={design} />
      {date && (
        <svg
          className="franked-stamp__mark"
          width={w + pad}
          height={h}
          viewBox={`0 0 ${60 + (60 * pad) / w} 70`}
          aria-hidden="true"
        >
          <PostmarkMarks date={date} cx={25} cy={42} r={16} />
        </svg>
      )}
    </span>
  );
}

/**
 * The letter on the road: a small envelope with its stamp in the corner, so the
 * stamp travels with the letter along the in-the-post track. Kept to simple
 * shapes — at this size a full stamp would be noise.
 */
export function TravellingLetter({ cls }: { cls: PostageClass }) {
  const art = ART[cls];
  return (
    <svg className={`travelling-letter travelling-letter--${cls}`} width="30" height="21" viewBox="0 0 30 21" aria-hidden="true">
      <rect x="0.75" y="0.75" width="28.5" height="19.5" rx="2.5" fill={PAPER} stroke="#2C2420" strokeWidth="1.1" />
      <path d="M1.5 2 L13 11 L16.5 11" fill="none" stroke="#2C2420" strokeWidth="1" strokeLinejoin="round" />
      <rect x="19.5" y="3" width="7.5" height="8.5" rx="0.6" fill={PAPER} stroke="#B9AFA6" strokeWidth="0.6" strokeDasharray="1 0.8" />
      <rect x="20.6" y="4.1" width="5.3" height="6.3" rx="0.4" fill={art.panel} />
    </svg>
  );
}

/**
 * Special editions (slice 4): commemorative stamps, never postage. Same
 * perforated sheet and flat panel as the class stamps — no shading, no tilt —
 * in their own colours. The welcome stamp carries an envelope with a heart;
 * a milestone carries its number.
 */
export type SpecialStampKind = 'welcome' | 'milestone-1' | 'milestone-5' | 'milestone-10';

const SPECIAL_ART: Record<SpecialStampKind, { panel: string; frame: string; ink: string; sub: string; word: string }> = {
  welcome: { panel: '#F6DCCD', frame: '#C8907C', ink: '#7B5244', sub: '#A87060', word: 'WELCOME' },
  'milestone-1': { panel: '#5E9A72', frame: '#B9D8C2', ink: '#F4FAF5', sub: '#D5EADB', word: 'FRIEND' },
  'milestone-5': { panel: '#2F4E7A', frame: '#8FA9CF', ink: '#F4F7FC', sub: '#C9D6EA', word: 'SORTING' },
  'milestone-10': { panel: '#2C2420', frame: '#E0B04A', ink: '#F5D78A', sub: '#E8C77A', word: 'POSTMASTER' },
};

export function SpecialStamp({ kind, size = 'sheet' }: { kind: SpecialStampKind; size?: StampSize }) {
  const id = useId().replace(/:/g, '');
  const art = SPECIAL_ART[kind];
  const w = SIZES[size];
  const h = Math.round((w * 70) / 60);
  const holes: [number, number][] = [];
  for (let x = 4; x <= 56; x += 6.5) holes.push([x, 0], [x, 70]);
  for (let y = 4; y <= 66; y += 6.5) holes.push([0, y], [60, y]);
  const number = kind === 'welcome' ? null : kind.split('-')[1];

  return (
    <svg
      className={`postage-stamp special-stamp special-stamp--${kind} postage-stamp--${size}`}
      width={w}
      height={h}
      viewBox="0 0 60 70"
      aria-hidden="true"
    >
      <defs>
        <mask id={`perf-${id}`}>
          <rect width="60" height="70" fill="#fff" />
          {holes.map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="2.7" fill="#000" />
          ))}
        </mask>
      </defs>
      <g mask={`url(#perf-${id})`}>
        <rect width="60" height="70" fill={PAPER} />
        <rect x="5" y="5" width="50" height="60" rx="1.5" fill={art.panel} />
        <rect x="8.5" y="8.5" width="43" height="53" rx="1" fill="none" stroke={art.frame} strokeWidth="1" />
        {number ? (
          <text
            x="30"
            y="40"
            textAnchor="middle"
            fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
            fontWeight="900"
            fontSize={number.length > 1 ? 22 : 26}
            fill={art.ink}
          >
            {number}
          </text>
        ) : (
          // An envelope with a heart for a seal.
          <g>
            <rect x="17" y="21" width="26" height="18" rx="1.5" fill="#FFFDF9" stroke={art.ink} strokeWidth="1.4" />
            <path d="M17.7 22 L30 31.5 L42.3 22" fill="none" stroke={art.ink} strokeWidth="1.4" strokeLinejoin="round" />
            <path
              d="M30 36.8 C27.4 34.9 25.9 33.6 25.9 32 C25.9 30.8 26.8 30 27.9 30 C28.8 30 29.5 30.5 30 31.2 C30.5 30.5 31.2 30 32.1 30 C33.2 30 34.1 30.8 34.1 32 C34.1 33.6 32.6 34.9 30 36.8 Z"
              fill={art.ink}
            />
          </g>
        )}
        <text
          x="30"
          y="56"
          textAnchor="middle"
          fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
          fontWeight="700"
          fontSize={art.word.length > 8 ? 5 : 6}
          letterSpacing="0.8"
          fill={art.sub}
        >
          {art.word}
        </text>
      </g>
    </svg>
  );
}
